import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, FlaskConical, Send } from 'lucide-react';
import type { ResolvedSapField, SapPushRunSummary, SapReadinessReport } from '@hcc/types';
import { Button } from '../../../../components/ui/Button';
import { ConfirmModal } from '../../../../components/ui/ConfirmModal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { api, ApiError } from '../../../../lib/api';
import { SapReviewCard } from './SapReviewCard';
import { SapEmploymentCard } from './SapEmploymentCard';
import { SapRunLog } from './SapRunLog';
import { SapDryRunModal, type DryRunStep } from './SapDryRunModal';

interface ReviewResponse {
  fields: ResolvedSapField[];
  readiness: SapReadinessReport;
  already_pushed: boolean;
  overridden: boolean;
}

interface DryRunResponse {
  run_id: string;
  status: string;
  steps: DryRunStep[];
}

/**
 * Per-person SAP push: review what will be sent, fix the employment
 * record, dry run, then push. The push button stays disabled until the
 * person is ready AND a dry run has completed, so nobody fires an
 * irreversible LIVE hire unseen.
 */
export function SapTab({ personId, personName }: { personId: string; personName: string }) {
  const queryClient = useQueryClient();
  const [dryRunResult, setDryRunResult] = useState<DryRunResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [overrideConfirm, setOverrideConfirm] = useState(false);

  const review = useQuery({
    queryKey: ['sap-review', personId],
    queryFn: () => api<ReviewResponse>(`/api/admin/sap/people/${personId}/review`),
  });
  const runs = useQuery({
    queryKey: ['sap-runs', personId],
    queryFn: () => api<{ runs: SapPushRunSummary[] }>(`/api/admin/sap/people/${personId}/runs`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sap-runs', personId] });
    void queryClient.invalidateQueries({ queryKey: ['sap-review', personId] });
  };

  const dryRun = useMutation({
    mutationFn: () =>
      api<DryRunResponse>(`/api/admin/sap/people/${personId}/dry-run`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (r) => {
      setDryRunResult(r);
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'dry run failed'),
  });

  const push = useMutation({
    mutationFn: () =>
      api<{ run_id: string }>(`/api/admin/sap/people/${personId}/push`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      toast.success('Push started; follow it in the run log below');
      setConfirmOpen(false);
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'push failed'),
  });

  // Admin override for re-hires / pre-existing records: mark the person
  // complete in SAP + Planon without pushing (or clear that override).
  const override = useMutation({
    mutationFn: (done: boolean) =>
      api(`/api/admin/sap/people/${personId}/override-status`, {
        method: 'POST',
        body: JSON.stringify({ done }),
      }),
    onSuccess: (_r, done) => {
      toast.success(done ? `${personName} marked as complete` : 'Override removed');
      setOverrideConfirm(false);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['sap-people-status'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'override failed'),
  });

  if (review.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (!review.data) {
    return <p className="text-sm text-fg-muted">Could not load the SAP review for this person.</p>;
  }

  const { readiness, already_pushed, overridden } = review.data;
  const hasDryRun = (runs.data?.runs ?? []).some((r) => r.status === 'dry_run_complete');
  const pushDisabled = !readiness.ready || already_pushed || !hasDryRun || push.isPending;

  return (
    <div className="space-y-4">
      {overridden ? (
        <div className="flex items-start justify-between gap-3 rounded-md bg-success-soft/15 px-4 py-3 text-sm text-success-soft-fg">
          <span className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Marked complete by an admin override. This person already exists in SAP and Planon, so
              the pipeline shows them done and will not push them.
            </span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            loading={override.isPending}
            onClick={() => override.mutate(false)}
          >
            Remove override
          </Button>
        </div>
      ) : null}

      {already_pushed ? (
        <div className="rounded-md bg-success-soft/15 px-4 py-3 text-sm text-success-soft-fg">
          This person exists in SAP. A new push resumes any incomplete steps; the hire itself
          never re-runs.
        </div>
      ) : null}

      {!readiness.ready ? (
        <div className="rounded-md bg-warning-soft/15 px-4 py-3 text-sm text-warning-soft-fg">
          <div className="flex items-center gap-2 font-medium">
            <AlertCircle className="h-4 w-4" /> Not ready to push
          </div>
          <ul className="mt-1.5 list-inside list-disc space-y-0.5">
            {readiness.missing.map((m) => (
              <li key={m.field}>
                {m.label}: {m.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="md"
          iconLeft={<FlaskConical className="h-4 w-4" />}
          loading={dryRun.isPending}
          onClick={() => dryRun.mutate()}
        >
          Dry run
        </Button>
        <Button
          variant="primary"
          size="md"
          iconLeft={<Send className="h-4 w-4" />}
          disabled={pushDisabled}
          onClick={() => setConfirmOpen(true)}
        >
          Push to SAP
        </Button>
        {!overridden ? (
          <Button
            variant="ghost"
            size="md"
            iconLeft={<CheckCircle2 className="h-4 w-4" />}
            onClick={() => setOverrideConfirm(true)}
          >
            Mark as already created
          </Button>
        ) : null}
        {!hasDryRun && readiness.ready && !already_pushed ? (
          <span className="text-xs text-fg-muted">Run a dry run first to enable the push.</span>
        ) : null}
      </div>

      <SapEmploymentCard personId={personId} />
      <SapReviewCard fields={review.data.fields} personId={personId} />
      <SapRunLog personId={personId} />

      {dryRunResult ? (
        <SapDryRunModal steps={dryRunResult.steps} onClose={() => setDryRunResult(null)} />
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        tone="warning"
        icon={Send}
        title={`Push ${personName} to SAP LIVE?`}
        message={
          <>
            This creates a NEW employee in the LIVE SAP tenant and writes their work schedule,
            email, bank details, NI number and next of kin. The hire cannot be undone from here.
          </>
        }
        confirmLabel="Push to SAP"
        cancelLabel="Cancel"
        onConfirm={() => push.mutate()}
        onClose={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        open={overrideConfirm}
        tone="warning"
        icon={CheckCircle2}
        title={`Mark ${personName} as already created?`}
        message={
          <>
            Use this only when {personName} already exists in SAP and Planon (for example a re-hire
            with an existing employee record). It marks them complete on the dashboard and stops the
            pipeline pushing them. It does NOT create or change anything in SAP or Planon.
          </>
        }
        confirmLabel="Mark as complete"
        cancelLabel="Cancel"
        onConfirm={() => override.mutate(true)}
        onClose={() => setOverrideConfirm(false)}
      />
    </div>
  );
}
